import {Button, Grid, useTheme} from "@mui/material";
import React, {useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {CellClassParams, ColDef, EditableCallbackParams, ICellRendererParams, RowNode} from "ag-grid-community";
import {DEFAULT_MATRIX_COL_DEF, maxNumberYupLimit, SET_SNACKBAR} from "../../../constants";
import {useTranslation} from "react-i18next";
import {GridApi} from "ag-grid-enterprise";
import {OWRouteDetail} from "../../../api/types/owRouteDetail";
import {useLocation, useNavigate} from "react-router-dom";
import CustomBanner from "../../../common/components/CustomBanner";
import {OWAncillaryEntityResponse} from "../../../api/types/owAncillary";
import {useOWAncillaryRepository} from "../../../api/repositories/OWAncillaryRepository";
import {useOWRouteDetailRepository} from "../../../api/repositories/OWRouteDetailRepository";
import dayjs from "dayjs";
import {DialogInterface} from "../../../api/types/generic";
import DialogComponent from "../../../common/components/DialogComponent";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import DateCustomEditorComponent from "../../../common/components/DateCustomEditorComponent";
import localizedFormat from "dayjs/plugin/localizedFormat";
import {useAncillaryRepository} from "../../../api/repositories/AncillaryRepository";
import NumberCustomEditorComponent from "../../../common/components/NumberCustomEditorComponent";
import BasicToolbar from "../../../common/components/BasicToolbar";
import {i18nConvertNumberWithDecimals} from "../../../utils";

dayjs.extend(localizedFormat);

const OWRouteMatrix = ({
                           owAncillaryId,
                           readOnly,
                           ancillaryInUse,
                           setValue
                       }: {
    owAncillaryId: number | null
    readOnly: boolean
    ancillaryInUse: boolean
    setValue: Function

}): JSX.Element => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Theme
    const theme = useTheme();

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use State
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    // Use Navigate
    const navigate = useNavigate();

    // Use State
    const [, setOWAncillary] = useState<OWAncillaryEntityResponse | null>(null);
    const [showOnlyEditable, setShowOnlyEditable] = useState<boolean>(false);
    const [owRouteDetails, setOWRouteDetails] = useState<OWRouteDetail[]>([]);
    const [originalOWRouteDetails, setOriginalOWRouteDetails] = useState<OWRouteDetail[]>([]);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);
    const location = useLocation();

    // Use Repository Hooks
    const {
        getOWRouteDetails,
        getOWRouteDetailsFromAncillary,//CHIAMARE QUESTO ENDPOINT INVECE CHE IL CLASSICO SE SI ARRIVA DA ANCILLARY E NON DA OW
        putOWRouteDetails
    } = useOWRouteDetailRepository();
    const {getOWAncillary} = useOWAncillaryRepository();
    const {getAncillary} = useAncillaryRepository();

    const getMatrixInfo = async () => {
        if (owAncillaryId) {
            try {
                let owRouteDetails: OWRouteDetail[] = [];
                const currentPath = location.pathname;

                if (currentPath.includes("ow")) {
                    const owAncillary = await getOWAncillary(owAncillaryId);
                    setOWAncillary(owAncillary);

                    owRouteDetails = await getOWRouteDetails(owAncillaryId);
                } else {
                    const owAncillary: OWAncillaryEntityResponse = {
                        id: -1,
                        prcAncillary: await getAncillary(owAncillaryId)
                    };
                    setOWAncillary(owAncillary);
                    owRouteDetails = await getOWRouteDetailsFromAncillary(owAncillaryId);
                }

                setOWRouteDetails(owRouteDetails.map((owRouteDetail: OWRouteDetail) => {
                    return {
                        ...owRouteDetail,
                        amount: Number(owRouteDetail.amount) / 100
                    };
                }));
                setOriginalOWRouteDetails(JSON.parse(JSON.stringify(owRouteDetails.map((owRouteDetail: OWRouteDetail) => {
                    return {
                        ...owRouteDetail,
                        amount: Number(owRouteDetail.amount) / 100
                    };
                }))));
            } catch (err) {
            }
        }
    };

    // Use Effect
    useEffect(() => {
        getMatrixInfo();
        // eslint-disable-next-line
    }, []);

    // ************ AG GRID START ************ //
    const onGridReady = (params: any) => {
        setGridApi(params.api);
        params.api.setDatasource(owRouteDetails);
    };

    const cellValueSetter = (params: any) => {
        console.log(params)
        if (params?.newValue !== null && params?.newValue !== undefined) {
            params.newValue = (params.newValue + '').replace(/,/g, '.');
        }
        if (!Number(params.newValue) && params.newValue != 0) {
            return false;
        } else if (params.newValue <= maxNumberYupLimit && params.newValue >= 0) {
            params.data[params.colDef.field.split(".")[0]] = params.newValue;
            return true;
        } else return false;
    };

    const columnDefs: ColDef<OWRouteDetail>[] = [
        ...[1].map((el) => ({
            headerName: t("pricing:owRouteDetailMatrix.labels.locationGroup"),
            headerTooltip: t("pricing:owRouteDetailMatrix.labels.locationGroup"),
            children: [
                {
                    headerName: t("pricing:owRouteDetailMatrix.labels.from"),
                    headerTooltip: t("pricing:owRouteDetailMatrix.labels.from"),
                    field: "locationFromId",
                    flex: 1,
                    editable: false,
                    pinned: "left",
                    sortable: false,
                    cellRenderer: (params: ICellRendererParams<OWRouteDetail>) => params.data?.locationFrom.name
                },
                {
                    headerName: t("pricing:owRouteDetailMatrix.labels.to"),
                    headerTooltip: t("pricing:owRouteDetailMatrix.labels.to"),
                    field: "locationToId",
                    flex: 1,
                    editable: false,
                    pinned: "left",
                    cellRenderer: (params: ICellRendererParams<OWRouteDetail>) => params.data?.locationTo.name
                }
            ]
        })),
        ...[1].map(() => ({
            headerName: t("pricing:owRouteDetailMatrix.labels.amount"),
            headerTooltip: t("pricing:owRouteDetailMatrix.labels.amount"),
            field: "amount",
            flex: 1,
            editable: (params: EditableCallbackParams) => {
                const routeDetail = JSON.parse(JSON.stringify(originalOWRouteDetails)).find((owRD: OWRouteDetail) => owRD.id === params.data.id) as OWRouteDetail;
                return dayjs().diff(dayjs(routeDetail.to), "minutes") < 0 && Boolean(params.data?.editable) && !readOnly;
            },
            cellEditor: "amountCustomEditor",
            valueSetter: cellValueSetter,
            cellRenderer: (params: ICellRendererParams) => i18nConvertNumberWithDecimals(params.data.amount),
            cellStyle: (params: CellClassParams) => {
                const routeDetail = JSON.parse(JSON.stringify(originalOWRouteDetails)).find((owRD: OWRouteDetail) => owRD.id === params.data.id) as OWRouteDetail;
                return {
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    textAlign: "right",
                    padding: 5,
                    borderRight: "2px solid #E0E0E0",
                    backgroundColor: dayjs().diff(dayjs(routeDetail.to), "minutes") < 0 ? "" : theme.palette.neutral.light
                };
            }
        })),
        ...[1].map((el) => ({
            headerName: t("pricing:owRouteDetailMatrix.labels.date"),
            headerTooltip: t("pricing:owRouteDetailMatrix.labels.date"),
            children: [
                {
                    headerName: t("pricing:owRouteDetailMatrix.labels.from"),
                    headerTooltip: t("pricing:owRouteDetailMatrix.labels.from"),
                    field: "from",
                    flex: 1,
                    editable: (params: ICellRendererParams) => {
                        const routeDetail = JSON.parse(JSON.stringify(originalOWRouteDetails)).find((owRD: OWRouteDetail) => owRD.id === params.data.id) as OWRouteDetail;
                        return dayjs().diff(dayjs(routeDetail.to), "minutes") < 0 && Boolean(params.data?.editable) && !readOnly;
                    },
                    cellEditor: "dateCustomEditor",
                    cellRenderer: (params: ICellRendererParams<OWRouteDetail>) => dayjs(params.data?.from).format("L LT"),
                    cellStyle: (params: CellClassParams) => {
                        const routeDetail = JSON.parse(JSON.stringify(originalOWRouteDetails)).find((owRD: OWRouteDetail) => owRD.id === params.data.id) as OWRouteDetail;
                        return {
                            backgroundColor: dayjs().diff(dayjs(routeDetail.to), "minutes") < 0 ? "" : theme.palette.neutral.light
                        };
                    }
                },
                {
                    headerName: t("pricing:owRouteDetailMatrix.labels.to"),
                    headerTooltip: t("pricing:owRouteDetailMatrix.labels.to"),
                    field: "to",
                    flex: 1,
                    editable: (params: { data: { editable: any; }; }) => Boolean(params.data?.editable) && !readOnly,
                    cellEditor: "dateCustomEditor",
                    cellRenderer: (params: ICellRendererParams<OWRouteDetail>) => dayjs(params.data?.to).format("L LT")
                }
            ]
        }))
    ];

    const defaultColDef = DEFAULT_MATRIX_COL_DEF;
    const frameworkComponents = {
        dateCustomEditor: DateCustomEditorComponent,
        amountCustomEditor: NumberCustomEditorComponent
    };
    // ************ AG GRID - END ************ //

    const handleClose = () => {
        setDialog(null);
        navigate("../ow/ow-ancillary");
    };

    const getRowsFromAgGrid = (): OWRouteDetail[] => {
        const rowsFromAgGrid: OWRouteDetail[] = [];
        if (gridApi) gridApi.forEachNodeAfterFilterAndSort((node: RowNode<OWRouteDetail>) => rowsFromAgGrid.push({
            ...node.data,
            amount: (node.data as OWRouteDetail).amount * 100
        } as OWRouteDetail));
        return rowsFromAgGrid;
    };

    const handlePersistMatrix = async () => {
        const owRouteDetails: OWRouteDetail[] = getRowsFromAgGrid().filter(el => el.editable);

        // //SPOSTARE IL CONTROLLO SULLE ROTTE CON DATE NON VALIDE PRIMA DELL'APERTURA DEL DIALOG DI CONFERMA
        //  const detailWithDateInvalidIsPresent = owRouteDetails.find(rd => !dayjs(rd.from).isValid() || !dayjs(rd.to).isValid());
        //  if (!detailWithDateInvalidIsPresent) {
        try {
            await putOWRouteDetails(owRouteDetails);
            setDialog(null);
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            await getMatrixInfo();
            !owAncillaryId && navigate("../ow/ow-ancillary");
        } catch (err) {
            console.log(err);
        }
        // } else {
        //     dispatchGlobalData({
        //         type: setSnackbar,
        //         payload: {
        //             snackbar: {
        //                 message: "controllare le date inserite",
        //                 type: snackbarTypes.error,
        //                 show: true,
        //                 autoClose: true
        //             }
        //         }
        //     });
        // }
    };

    // set background colour on even rows again, this looks bad, should be using CSS classes
    const getRowStyle = (params: any) => {
        if (!params.data.editable) {
            return {backgroundColor: theme.palette.neutral.light};
        }
    };

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                activeSwitch={{
                    label: t("pricing:rentalPeriods.form.labels.showActive"),
                    handleChange: (checked: boolean) => {
                        setShowOnlyEditable(checked);
                        setOWRouteDetails(checked ? originalOWRouteDetails.filter(r => dayjs(r.to) > dayjs()) : originalOWRouteDetails);
                    },
                    value: showOnlyEditable as boolean
                }}
            />
        )
    };

    console.log(owRouteDetails)
    console.log(originalOWRouteDetails)


    return (
        <Grid container spacing={2} alignItems="start">
            {!readOnly && ancillaryInUse &&
                <CustomBanner message={t("pricing:owAncillaryMatrix.banner")} severity={"warning"}/>}
            <Grid item xs={12}>
                <AgGridCustomTable
                    rowData={owRouteDetails}
                    rowSelection={"multiple"}
                    suppressRowClickSelection={true}
                    enableRangeSelection={true}
                    enableCellTextSelection={false}
                    gridRef={gridRef}
                    pagination={false}
                    defaultColDef={defaultColDef}
                    columnDefs={columnDefs}
                    localeText={agGridLocale}
                    tooltipShowDelay={0}
                    domLayout="autoHeight"
                    onGridReady={onGridReady}
                    frameworkComponents={frameworkComponents}
                    getRowStyle={getRowStyle}
                    toolbar={toolbar}
                />
                {!readOnly &&
                    <Grid container item xs={12} justifyContent="space-between" columnGap={2} my={2}>
                        <Grid>
                            <Button color={"accent"}
                                    type={"button"}
                                    onClick={() => setDialog({
                                        open: true,
                                        message: t("pricing:dialogs.messages.looseChanging"),
                                        onClickConfirm: () => {
                                            handleClose()
                                        }
                                    })}
                            >
                                {t("pricing:actions.close")}
                            </Button>
                        </Grid>
                        <Grid>
                            {owAncillaryId &&
                                <Button variant="outlined"
                                        sx={{marginRight: "15px"}}
                                        color={"accent"}
                                        type={"button"}
                                        onClick={() => setDialog({
                                            open: true,
                                            message: t("pricing:dialogs.messages.looseChanging"),
                                            onClickConfirm: () => {
                                                window.close()
                                            }
                                        })}
                                >
                                    {t("pricing:actions.back")}
                                </Button>
                            }
                            <Button color={"accent"}
                                    type={"button"}
                                    onClick={() => setDialog({
                                        open: true,
                                        message: ancillaryInUse ?
                                            t("pricing:owAncillaryMatrix.banner")
                                            :
                                            t("pricing:ancillaryMatrix.dialogContents.createAncillaryMatrix"),
                                        onClickConfirm: handlePersistMatrix
                                    })}
                            >
                                {t("pricing:actions.confirm")}
                            </Button>
                        </Grid>
                    </Grid>
                }
            </Grid>
            {dialog?.open &&
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => setDialog(null)}
                    open={dialog.open}
                    message={dialog.message}
                />
            }
        </Grid>
    );
};

export default OWRouteMatrix;
